import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { Button } from "../components/ui/Button";
import { cn } from "../components/ui/cn";
import type { Automation, Pipeline, SavedView, User } from "../types";

/**
 * Triage rules, in the shape of Gmail filters: match some conditions, apply
 * some actions.
 *
 * Rules run only when a ticket is created, never on later replies -- a rule
 * that re-fired mid-conversation would reassign work out from under whoever was
 * doing it.
 */
export function Automations({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const isAdmin = user.role === "admin";
  const [editing, setEditing] = useState<Automation | "new" | null>(null);

  const rules = useQuery({
    queryKey: queryKeys.automations,
    queryFn: api.automations,
  });
  const users = useQuery({ queryKey: queryKeys.users, queryFn: api.users });
  const pipelines = useQuery({
    queryKey: queryKeys.pipelines,
    queryFn: api.pipelines,
  });
  const savedViews = useQuery({ queryKey: queryKeys.savedViews, queryFn: api.savedViews });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.automations });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteAutomation(id),
    onSuccess: invalidate,
  });

  return (
    <div className="mx-auto h-full w-full max-w-4xl overflow-y-auto p-4 sm:p-6">
      <div className="mb-6 flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="mb-1 text-lg font-semibold text-content">Automations</h1>
          <p className="text-sm text-muted">
            {isAdmin
              ? "Rules that assign and classify tickets as they arrive, or as they land in a view. Every condition you set must match; all matching rules apply in order."
              : "Rules that assign and classify tickets as they arrive, or as they land in a view. Read-only — ask an admin to change them."}
          </p>
        </div>
        {isAdmin ? (
          <Button onClick={() => setEditing("new")}>New rule</Button>
        ) : null}
      </div>

      {editing ? (
        <RuleEditor
          key={editing === "new" ? "new" : editing.id}
          rule={editing === "new" ? null : editing}
          users={users.data ?? []}
          pipelines={pipelines.data ?? []}
          savedViews={savedViews.data ?? []}
          nextPosition={(rules.data?.length ?? 0) * 10}
          onDone={() => {
            setEditing(null);
            invalidate();
          }}
          onCancel={() => setEditing(null)}
        />
      ) : null}

      {rules.data?.length === 0 && !editing ? (
        <p className="rounded-lg border border-dashed border-line p-8 text-center text-sm text-muted">
          No rules yet. Tickets arrive unassigned and unclassified.
        </p>
      ) : null}

      <ul className="space-y-2">
        {(rules.data ?? []).map((rule) => (
          <li
            key={rule.id}
            className={cn(
              "rounded-lg border border-line bg-surface p-3",
              !rule.isActive && "opacity-60",
            )}
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="truncate text-sm font-medium text-content">
                    {rule.name}
                  </span>
                  {!rule.isActive ? (
                    <span className="text-[10px] uppercase text-faint">Off</span>
                  ) : null}
                </div>

                <p className="mt-1 text-xs text-muted">
                  <span className="text-faint">When </span>
                  {describeConditions(rule, users.data ?? [], savedViews.data ?? [])}
                  <span className="text-faint"> → </span>
                  {describeActions(rule, users.data ?? [], pipelines.data ?? [])}
                </p>
              </div>

              {isAdmin ? (
                <div className="flex shrink-0 items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(rule)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (window.confirm(`Delete the rule “${rule.name}”?`)) {
                        remove.mutate(rule.id);
                      }
                    }}
                  >
                    Delete
                  </Button>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function describeConditions(
  rule: Automation,
  users: User[],
  savedViews: SavedView[],
): string {
  if (rule.viewKey) {
    return `a ticket lands in ${viewLabel(rule.viewKey, users, savedViews)}`;
  }

  const parts: string[] = [];
  if (rule.fromDomain) parts.push(`from domain is ${rule.fromDomain}`);
  if (rule.fromEmail) parts.push(`sender is ${rule.fromEmail}`);
  if (rule.toDomain) parts.push(`sent to domain ${rule.toDomain}`);
  if (rule.toEmail) parts.push(`sent to ${rule.toEmail}`);
  if (rule.subjectContains) {
    parts.push(`subject contains “${rule.subjectContains}”`);
  }
  if (rule.subjectAny?.length) {
    parts.push(
      rule.subjectAny.length === 1
        ? `subject contains “${rule.subjectAny[0]}”`
        : `subject contains any of ${rule.subjectAny
            .map((word) => `“${word}”`)
            .join(", ")}`,
    );
  }
  if (rule.bodyAny?.length) {
    parts.push(
      rule.bodyAny.length === 1
        ? `the email says “${rule.bodyAny[0]}”`
        : `the email says any of ${rule.bodyAny.map((word) => `“${word}”`).join(", ")}`,
    );
  }
  return parts.join(" and ");
}

/**
 * The views a rule can name, as the sidebar names them.
 *
 * Only the ones a ticket is put into by somebody doing something -- see
 * `automations/view-rules.ts` on the server. "Mine" and "My team" depend on
 * who is looking and a rule has no viewer; Overdue, and a saved view filtered
 * on the SLA clock, are entered by time passing, with no moment to fire at.
 */
function viewOptions(users: User[], savedViews: SavedView[]): { key: string; label: string }[] {
  return [
    { key: "unassigned", label: "Unassigned" },
    { key: "spam", label: "Spam" },
    ...users.map((person) => ({ key: `user-${person.id}`, label: `${person.name}'s tickets` })),
    ...savedViews
      .filter((view) => !view.slaState)
      .map((view) => ({ key: `saved-${view.id}`, label: view.name })),
  ];
}

function viewLabel(key: string, users: User[], savedViews: SavedView[]): string {
  return viewOptions(users, savedViews).find((option) => option.key === key)?.label ?? "a view";
}

/**
 * Words the desk uses for the same thing.
 *
 * A fixed vocabulary rather than a thesaurus: what matters is what customers
 * write to *this* desk, and "quote / quotation / pricing" is a better answer
 * for that than anything a general word list would give. Every family is
 * symmetric -- naming any member offers the rest.
 *
 * Nothing here is a case variant. Matching is case-insensitive on both sides,
 * so offering "Quote" beside "quote" would be offering the same word twice.
 */
const WORD_FAMILIES: string[][] = [
  ["quote", "quotation", "pricing", "price", "cost", "estimate"],
  ["stock", "availability", "lead time", "backorder", "eta", "in stock"],
  ["order", "purchase order", "po number", "order number", "confirmation"],
  ["delivery", "shipping", "dispatch", "tracking", "courier", "delivered"],
  ["return", "rma", "refund", "replacement", "warranty", "faulty"],
  ["invoice", "billing", "payment", "remittance", "credit note", "vat"],
  ["fault", "faulty", "not working", "broken", "issue", "problem"],
  ["cancel", "cancellation", "amend", "change order"],
  ["quote chase", "chase", "follow up", "any update"],
];

/** Related words worth offering, given what has been added already. */
export function relatedWords(current: string[]): string[] {
  const have = new Set(current.map((word) => word.trim().toLowerCase()));
  const out: string[] = [];

  for (const family of WORD_FAMILIES) {
    // The family is offered when any of its members is already listed, which
    // is what makes one typed word turn into the set the desk actually uses.
    if (!family.some((word) => have.has(word))) continue;

    for (const word of family) {
      if (!have.has(word) && !out.includes(word)) out.push(word);
    }
  }

  return out;
}

function describeActions(
  rule: Automation,
  users: User[],
  pipelines: Pipeline[],
): string {
  const parts: string[] = [];
  if (rule.assignToUserId) {
    const name =
      users.find((entry) => entry.id === rule.assignToUserId)?.name ?? "someone";
    parts.push(`assign to ${name}`);
  }
  if (rule.setPipelineId) {
    const name =
      pipelines.find((entry) => entry.id === rule.setPipelineId)?.name ??
      "a pipeline";
    parts.push(`set pipeline to ${name}`);
  } else if (rule.clearPipeline) {
    parts.push("take it out of any pipeline");
  }
  return parts.join(", ");
}

function RuleEditor({
  rule,
  users,
  pipelines,
  savedViews,
  nextPosition,
  onDone,
  onCancel,
}: {
  rule: Automation | null;
  users: User[];
  pipelines: Pipeline[];
  savedViews: SavedView[];
  nextPosition: number;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(rule?.name ?? "");
  /*
   * Which kind of rule this is. Two kinds rather than a view as one more
   * condition beside the others: a view rule fires long after the email
   * arrived, so a sender or subject beside it would be tested against
   * nothing, and the server refuses the mix.
   */
  const [trigger, setTrigger] = useState<"mail" | "view">(rule?.viewKey ? "view" : "mail");
  const [viewKey, setViewKey] = useState(rule?.viewKey ?? "");
  const [bodyAny, setBodyAny] = useState<string[]>(rule?.bodyAny ?? []);
  const [fromDomain, setFromDomain] = useState(rule?.fromDomain ?? "");
  const [fromEmail, setFromEmail] = useState(rule?.fromEmail ?? "");
  const [toDomain, setToDomain] = useState(rule?.toDomain ?? "");
  const [toEmail, setToEmail] = useState(rule?.toEmail ?? "");
  const [subjectAny, setSubjectAny] = useState<string[]>(
    /*
     * Old single-substring rules are shown in the same field. The migration
     * moved the stored ones across; this covers a row written straight into
     * the database since.
     */
    rule?.subjectAny?.length
      ? rule.subjectAny
      : rule?.subjectContains
        ? [rule.subjectContains]
        : [],
  );
  const [assignToUserId, setAssignToUserId] = useState<string>(
    rule?.assignToUserId ? String(rule.assignToUserId) : "",
  );
  /* "none" for a rule that takes the ticket out of any pipeline. */
  const [setPipelineId, setSetPipelineId] = useState<string>(
    rule?.setPipelineId ? String(rule.setPipelineId) : rule?.clearPipeline ? "none" : "",
  );
  const [isActive, setIsActive] = useState(rule?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        position: rule?.position ?? nextPosition,
        isActive,
        /* The other kind's conditions are sent empty, not as last typed: a
           rule switched from email to view keeps nothing it no longer shows. */
        ...(trigger === "view"
          ? {
              viewKey: viewKey || null,
              fromDomain: null,
              fromEmail: null,
              toDomain: null,
              toEmail: null,
              subjectContains: null,
              subjectAny: [],
              bodyAny: [],
            }
          : {
              viewKey: null,
              fromDomain: fromDomain.trim() || null,
              fromEmail: fromEmail.trim() || null,
              toDomain: toDomain.trim() || null,
              toEmail: toEmail.trim() || null,
              // Cleared, because the editor now shows the old value in the list --
              // leaving it set would AND a condition the form no longer displays.
              subjectContains: null,
              subjectAny,
              bodyAny,
            }),
        assignToUserId: assignToUserId ? Number(assignToUserId) : null,
        setPipelineId: setPipelineId && setPipelineId !== "none" ? Number(setPipelineId) : null,
        clearPipeline: setPipelineId === "none",
      };
      return rule
        ? api.updateAutomation(rule.id, payload)
        : api.createAutomation(payload);
    },
    onSuccess: onDone,
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not save"),
  });

  const hasCondition =
    trigger === "view"
      ? Boolean(viewKey)
      : Boolean(
          fromDomain.trim() ||
            fromEmail.trim() ||
            toDomain.trim() ||
            toEmail.trim() ||
            subjectAny.length ||
            bodyAny.length,
        );
  const hasAction = Boolean(assignToUserId || setPipelineId);
  const canSave = name.trim() && hasCondition && hasAction && !save.isPending;

  /*
   * Why the button is off, when it is.
   *
   * It used to sit there disabled with the explanations hidden behind having
   * typed a name -- so filling in a subject and an action and getting a dead
   * button told you nothing, and the missing field was the one at the top you
   * had scrolled past.
   */
  const blocker = !name.trim()
    ? "Give the rule a name."
    : !hasCondition
      ? trigger === "view"
        ? "Choose the view."
        : "Add at least one condition — otherwise this rule would match every ticket."
      : !hasAction
        ? "Add at least one action — otherwise this rule would do nothing."
        : null;

  return (
    <div className="mb-6 rounded-lg border border-line bg-surface p-4">
      <Field label="Rule name">
        <input
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Installer.com orders to Technical Support"
          className={inputClass}
        />
      </Field>

      <div className="mt-4 flex flex-wrap gap-1.5" role="radiogroup" aria-label="When the rule runs">
        {(
          [
            ["mail", "When an email arrives"],
            ["view", "When a ticket lands in a view"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={trigger === value}
            onClick={() => setTrigger(value)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs",
              trigger === value
                ? "border-brand-600 bg-brand-600 text-white"
                : "border-line text-muted hover:bg-surface-2 hover:text-content",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {trigger === "view" ? (
        <>
          <div className="mt-3 max-w-sm">
            <Field label="When a ticket lands in">
              <select
                value={viewKey}
                onChange={(event) => setViewKey(event.target.value)}
                className={inputClass}
              >
                <option value="">Choose a view…</option>
                {viewOptions(users, savedViews).map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <p className="mt-2 text-xs text-faint">
            Runs when a ticket arrives in the view, or is assigned, marked as
            spam, reopened or moved into it — not again while it stays there,
            so a pipeline changed by hand afterwards is left alone.
          </p>
        </>
      ) : null}

      {trigger === "mail" ? (
      <>
      <p className="mb-2 mt-4 text-[11px] font-medium uppercase tracking-wide text-faint">
        When a ticket arrives and…
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="From domain is">
          <input
            value={fromDomain}
            onChange={(event) => setFromDomain(event.target.value)}
            placeholder="installer.com"
            className={inputClass}
          />
        </Field>
        <Field label="From email is">
          <input
            value={fromEmail}
            onChange={(event) => setFromEmail(event.target.value)}
            placeholder="orders@installer.com"
            className={inputClass}
          />
        </Field>
        <Field label="Sent to domain">
          <input
            value={toDomain}
            onChange={(event) => setToDomain(event.target.value)}
            placeholder="netxl.com"
            className={inputClass}
          />
        </Field>
        <Field label="Sent to email">
          <input
            value={toEmail}
            onChange={(event) => setToEmail(event.target.value)}
            placeholder="returns@netxl.com"
            className={inputClass}
          />
        </Field>
      </div>

      <div className="mt-3">
        <span className="mb-1 block text-xs text-muted">
          Subject contains any of
        </span>
        <SubjectKeywords value={subjectAny} onChange={setSubjectAny} />
      </div>

      <div className="mt-3">
        <span className="mb-1 block text-xs text-muted">
          Email body contains any of
        </span>
        <SubjectKeywords value={bodyAny} onChange={setBodyAny} placeholder="rma" />
      </div>

      <p className="mt-2 text-xs text-faint">
        Leave a box empty to ignore it. Everything you fill in must match,
        except the words in each list — any one of those is enough. Matching
        ignores capitals, so “quote” already covers “Quote”. “Sent to” also
        matches Cc and the channel the message arrived at. The body is the
        first message only, as plain text.
      </p>
      </>
      ) : null}

      <p className="mb-2 mt-4 text-[11px] font-medium uppercase tracking-wide text-faint">
        Then…
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Assign to">
          <select
            value={assignToUserId}
            onChange={(event) => setAssignToUserId(event.target.value)}
            className={inputClass}
          >
            <option value="">Leave unassigned</option>
            {users.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Set pipeline to">
          <select
            value={setPipelineId}
            onChange={(event) => setSetPipelineId(event.target.value)}
            className={inputClass}
          >
            <option value="">Leave unset</option>
            <option value="none">No pipeline</option>
            {pipelines.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <label className="mt-4 flex items-center gap-2 text-xs text-muted">
        <input
          type="checkbox"
          checked={isActive}
          onChange={(event) => setIsActive(event.target.checked)}
          className="accent-brand-600"
        />
        Rule is on
      </label>

      {blocker ? <p className="mt-2 text-xs text-amber-600">{blocker}</p> : null}
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}

      <div className="mt-4 flex items-center gap-2">
        <Button onClick={() => save.mutate()} disabled={!canSave}>
          {save.isPending ? "Saving…" : rule ? "Save changes" : "Create rule"}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

const inputClass = cn(
  "w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
);

/**
 * A word-list condition -- the subject's or the body's -- any one of which
 * matches.
 *
 * Chips rather than a comma-separated box, for the same reason the recipient
 * fields are chips -- a word is either in the rule or it is not, and loose text
 * cannot show you which.
 */
function SubjectKeywords({
  value,
  onChange,
  placeholder = "quote",
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [text, setText] = useState("");

  const add = (word: string): void => {
    const trimmed = word.trim();
    if (!trimmed) return;
    // Case-insensitively, because that is how it will be matched: adding
    // "Quote" beside "quote" would be adding the same condition twice.
    const have = value.map((entry) => entry.toLowerCase());
    if (!have.includes(trimmed.toLowerCase())) onChange([...value, trimmed]);
    setText("");
  };

  const suggestions = relatedWords(value).slice(0, 8);

  return (
    <div>
      <div
        className={cn(
          "flex min-h-[2.1rem] flex-wrap items-center gap-1 rounded-md border border-line bg-surface px-1.5 py-1",
          "focus-within:ring-2 focus-within:ring-brand-500/40",
        )}
      >
        {value.map((word) => (
          <span
            key={word}
            className="flex items-center gap-1 rounded bg-surface-2 py-0.5 pl-1.5 pr-1 text-xs text-content"
          >
            {word}
            <button
              type="button"
              onClick={() => onChange(value.filter((entry) => entry !== word))}
              aria-label={`Remove ${word}`}
              className="flex size-4 items-center justify-center rounded text-faint hover:bg-surface hover:text-brand-600 dark:hover:text-brand-400"
            >
              ✕
            </button>
          </span>
        ))}

        <input
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === ",") {
              event.preventDefault();
              add(text);
              return;
            }
            if (event.key === "Backspace" && text === "" && value.length > 0) {
              event.preventDefault();
              onChange(value.slice(0, -1));
            }
          }}
          // Committing on the way out is what stops a typed word being lost to
          // a click on Create rule.
          onBlur={() => add(text)}
          placeholder={value.length === 0 ? placeholder : ""}
          className="min-w-[6rem] flex-1 bg-transparent px-1 py-0.5 text-sm text-content outline-none placeholder:text-faint"
        />
      </div>

      {suggestions.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          <span className="text-[11px] text-faint">Also used for this:</span>
          {suggestions.map((word) => (
            <button
              key={word}
              type="button"
              onClick={() => add(word)}
              className="rounded border border-line px-1.5 py-0.5 text-[11px] text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400"
            >
              + {word}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-muted">{label}</span>
      {children}
    </label>
  );
}
